// ============================================================
// fondos/efectos/matrix-rain.js — «Matrix»: columnas de caracteres que caen dejando estela (efecto de
// LIENZO con ESTELA: el lienzo no se limpia entre fotogramas)
//
// Portado de `vendor/bg/matrix-rain-bg.js` en la F2b del P3: la clase encadenaba un `setTimeout` por
// paso (y, al pausar dentro de esa espera, dos bucles: ×11,6 en la línea base); ahora los pasos salen
// del `dt` del motor, al MISMO ritmo que se ve hoy (`periodoDeLluvia`).
//
// Rehecho en la F3 (E2): cada paso tendía un velo NEGRO (oscuro) o BLANCO (claro) al 5 % sobre lo
// anterior, y ese velo se acumulaba hasta tapar la sección entera (en oscuro, negra en unos
// segundos: medido 98 % de la sección alterada). Ahora la estela se DESVANECE A TRANSPARENTE: cada
// paso le quita a lo pintado un 5 % de su alfa (`destination-out`), así que lo que queda debajo es
// el color de la sección. Con UN residuo, acotado: el lienzo guarda el alfa en 8 bits y 9 × 0,95
// redondea a 9, así que lo ya desvanecido se queda en 9/255 (3,5 %). Medido en Chrome 153 (F3,
// tramo 2): a los 2 min ese residuo cubre el 40 % de la sección, el alfa medio se estanca en 0,038
// desde el minuto y ya no crece (en HEAD, la sección acababa negra). Los caracteres van al tope del
// catálogo (`alfaMax`).
// ============================================================

import { rgbDe } from './comun.js';

const CARACTERES = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789$+-*/=%\"'#&_(),.;:?!\\|{}<>[]^~";

/** Un fotograma a 60 Hz (ms): la pantalla con la que se calibra el ritmo de hoy. */
const FOTOGRAMA_60 = 1000 / 60;

/** Pasos como mucho por fotograma: tras un parón no se recupera el tiempo perdido de golpe. */
const MAX_PASOS = 8;

/**
 * Cada cuántos ms cae una fila, tal como se ve HOY en una pantalla de 60 Hz. La clase esperaba
 * `frameDelay` ms (velocidad 1-10 → 120-8 ms; sin dato, 5 → 70 ms) con un `setTimeout` y luego
 * pedía el fotograma SIGUIENTE, así que cada paso duraba el primer múltiplo de 16,7 ms que supera
 * `frameDelay` (+1 ms de lo que tarda el propio paso): velocidad 5 → 83,3 ms (12 pasos/s, medido
 * 11,2 en la línea base con la máquina cargada), 1 → 133 ms, 10 → 16,7 ms.
 *
 * @param {number} [speed] - 1-10, tal cual lo guarda el dato (sin él, el defecto de la clase: 5)
 * @returns {number} ms
 */
export function periodoDeLluvia(speed) {
    const v = speed != null ? speed : 5;
    const frameDelay = Math.round(120 - (v - 1) * (112 / 9));
    return Math.ceil((frameDelay + 1) / FOTOGRAMA_60) * FOTOGRAMA_60;
}

/** Cuánto alfa le quita cada paso a lo ya pintado: la longitud de la estela (la de siempre). */
export const DESVANECE = 0.05;

/**
 * Una gota por columna, cada una por encima del borde superior (entran poco a poco). Las columnas que
 * ya existían conservan la suya: al re-medirse la sección (una foto que carga y la estira) la lluvia
 * sigue donde iba en vez de volver a empezar.
 */
function gotas(s) {
    s.columnas = Math.floor(s.w / s.fontSize);
    const antes = s.drops || [];
    s.drops = [];
    for (let i = 0; i < s.columnas; i++) {
        s.drops[i] = i < antes.length ? antes[i] : s.azar() * -100;
    }
}

/**
 * UN paso de la lluvia: la estela se desvanece (a TRANSPARENTE) y cae un carácter al azar en cada
 * columna. Exportado para los tests (el ritmo lo pone `dibujar`).
 */
export function pasoDeLluvia(s, ctx) {
    const rgb = s.rgb;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = `rgba(0, 0, 0, ${DESVANECE})`;
    ctx.fillRect(0, 0, s.w, s.h);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${s.alfa})`;
    ctx.font = `${s.fontSize}px monospace`;
    for (let i = 0; i < s.drops.length; i++) {
        const text = CARACTERES.charAt(Math.floor(s.azar() * CARACTERES.length));
        ctx.fillText(text, i * s.fontSize, s.drops[i] * s.fontSize);
        // La gota que ya cruzó la sección vuelve arriba, de vez en cuando.
        if (s.drops[i] * s.fontSize > s.h && s.azar() > 0.975) {
            s.drops[i] = 0;
        }
        s.drops[i]++;
    }
}

export default {
    estela: true,

    /** Para el fotograma QUIETO: 150 pasos, lo que tarda la lluvia en llenar la sección. */
    precalentado(s) {
        return 150 * s.periodo;
    },

    crear(ctx, e) {
        const p = e.params;
        // Densidad 1-10 → letra de 28 a 12 px (más pequeña, más columnas); 22 px en táctil.
        const density = p.density != null ? p.density : 5;
        const fontSize = e.tactil ? 22 : Math.round(28 - (density - 1) * (16 / 9));
        const periodo = periodoDeLluvia(p.speed);
        const s = {
            w: e.w, h: e.h, azar: e.azar, rgb: rgbDe(e.c1),
            alfa: e.alfa, fontSize: fontSize, periodo: periodo,
            // Tiempo desde el último paso: arranca en un periodo, así que el primer fotograma pinta.
            desde: periodo,
            columnas: 0, drops: null,
        };
        gotas(s);
        return s;
    },

    /**
     * Re-medirse deja el lienzo en blanco (lo hace el navegador): la estela vuelve a crecer desde las
     * gotas, que siguen donde iban (`gotas`).
     */
    redimensionar(s, w, h) {
        s.w = w;
        s.h = h;
        gotas(s);
    },

    dibujar(s, ctx, t, dt) {
        if (!(dt > 0)) return;
        s.desde += dt;
        let n = 0;
        while (s.desde >= s.periodo && n < MAX_PASOS) {
            pasoDeLluvia(s, ctx);
            s.desde -= s.periodo;
            n++;
        }
        if (s.desde >= s.periodo) s.desde = 0;
    },
};
